import type { IGenericPaymentItem } from '../components/GenericPaymentModal';
import type { ICurrencyInfo } from '../redux/checkout/types';
import type { ISaleObject } from '../types/global/helper';
import { completeSandboxPurchase } from '../redux/sandbox/actions';
import { dispatchPromise, showToastMessage } from './AppUtils';
import { resolveSandboxMediaUrl, type SandboxCourse } from './sandboxCourseApi';

export type SandboxFeePreview = {
    catalog_cents?: number;
    customer_fees_cents?: number;
    final_amount_cents?: number;
    processor_fee_profile?: string;
    itemized?: Array<{
        type?: string;
        description?: string;
        amount_cents?: number;
        rate?: number;
        charged_to?: string;
    }>;
};

export type SandboxPricing = {
    price_cents: number;
    subscriber_discount_applied?: boolean;
    free?: boolean;
    requires_pro_pass?: boolean;
    fee_preview?: SandboxFeePreview;
    processor_fee_profile?: string;
    student_total_cents?: number;
};

export type SandboxCoursePaymentPayload = {
    items: IGenericPaymentItem[];
    saleObject: ISaleObject;
    currencyInfo: ICurrencyInfo;
    additionalFees?: Array<{ label: string; amount: number; description?: string }>;
};

export function additionalFeesFromPreview(preview?: SandboxFeePreview | null) {
    const lines: Array<{ label: string; amount: number; description?: string }> = [];
    let processing = 0;
    for (const row of preview?.itemized || []) {
        const amount = Math.round(Number(row.amount_cents) || 0);
        if (amount <= 0) continue;
        if (row.type === 'stripe_fees' || row.type === 'stripe_fixed_fee') {
            processing += amount;
            continue;
        }
        if (row.type === 'percentage_fee') {
            lines.push({
                label: row.rate ? `Platform fee (${row.rate}%)` : 'Platform fee',
                amount,
            });
            continue;
        }
        lines.push({ label: row.description || 'Fee', amount, description: row.type });
    }
    if (processing > 0) {
        lines.push({ label: 'Payment processing', amount: processing });
    }
    return lines;
}

function sandboxSaleMetadata(extra: Record<string, unknown>) {
    return {
        customer_pays_service_charge: true,
        override_tax_percent: 0,
        ...extra,
    };
}

/**
 * Builds items + saleObject for GenericPaymentModal (signed-in payment sheet).
 * Does not open the full event CheckoutModal.
 */
export function buildSandboxCoursePayment(params: {
    course: SandboxCourse;
    pricing: SandboxPricing;
    currency?: string;
    conversion?: {
        original_currency?: string;
        transaction_currency?: string;
        rate?: number;
        converted_amount?: number;
        formatted_for_display?: string | null;
        defaulted_to_usd?: boolean;
    } | null;
}): SandboxCoursePaymentPayload | null {
    const { course, pricing } = params;
    const original = (params.currency || course.currency || 'USD').toLowerCase();
    const priceCents = Math.round(Number(pricing.price_cents) || 0);
    if (priceCents <= 0) {
        showToastMessage('This course does not require payment', 'info');
        return null;
    }

    const transaction = (params.conversion?.transaction_currency || original).toLowerCase();
    const rate = Number(params.conversion?.rate) || 1;
    const useLocal =
        Boolean(params.conversion?.formatted_for_display) &&
        transaction !== original &&
        rate > 0 &&
        !params.conversion?.defaulted_to_usd;
    const chargeCents = useLocal
        ? Math.round((Number(params.conversion?.converted_amount) || (priceCents / 100) * rate) * 100)
        : priceCents;
    const chargeCurrency = useLocal ? transaction : original;
    const additionalFees = additionalFeesFromPreview(pricing.fee_preview);
    const processorFeeProfile = pricing.processor_fee_profile || pricing.fee_preview?.processor_fee_profile;

    const items: IGenericPaymentItem[] = [
        {
            id: course._id,
            title: course.title,
            description: course.description || 'Creators Sandbox masterclass',
            original_price: chargeCents,
            price: chargeCents,
            currency: chargeCurrency,
            quantity: 1,
            image: resolveSandboxMediaUrl(course.cover_image) || undefined,
        },
    ];

    const saleObject: ISaleObject = {
        category: 'sandbox_course',
        item: items,
        transaction_currency: chargeCurrency,
        linked_event: course._id,
        linked_event_type: 'SandboxCourse',
        max_items: 1,
        ...({
            metadata: sandboxSaleMetadata({
                ...(pricing.subscriber_discount_applied ? { subscriber_discount_applied: true } : {}),
                ...(processorFeeProfile ? { processor_fee_profile: processorFeeProfile } : {}),
                catalog_cents: priceCents,
            }),
        } as any),
    };

    const currencyInfo: ICurrencyInfo = {
        transaction_currency: chargeCurrency,
        original_currency: original,
        rate: useLocal ? rate : 1,
    };

    return { items, saleObject, currencyInfo, additionalFees };
}

export async function finalizeSandboxPurchaseFromSale(
    courseId: string,
    sale: { _id?: string; price?: number; transaction_currency?: string },
    subscriberDiscountApplied?: boolean,
) {
    if (!sale?._id) return;
    await dispatchPromise(
        completeSandboxPurchase({
            courseId,
            body: {
                saleId: sale._id,
                paid_cents: sale.price,
                currency: sale.transaction_currency,
                subscriber_discount_applied: subscriberDiscountApplied,
            },
        }),
    );
}
